<template>
  <section class="root-detail-page">
    <header class="detail-hero">
      <button type="button" class="back-btn" @click="goBack">{{ t("back") }}</button>

      <div class="hero-copy">
        <p class="hero-eyebrow">{{ t("eyebrow") }}</p>
        <h1 class="hero-title">{{ root.root_text }}</h1>
        <div class="hero-meta">
          <span>{{ root.root_id }}</span>
          <span>{{ root.kind || "-" }}</span>
        </div>
      </div>

      <div class="lang-pill">
        <button
          v-for="choice in langs"
          :key="choice"
          type="button"
          :class="['lang-chip', { active: choice === lang }]"
          @click="setLang(choice)"
        >
          {{ choice.toUpperCase() }}
        </button>
      </div>
    </header>

    <div v-if="loading" class="state-card">{{ t("loading") }}</div>
    <div v-else-if="errorMessage" class="state-card error-card">{{ errorMessage }}</div>
    <template v-else>
      <section class="content-grid">
        <article class="panel-card">
          <div class="section-head">
            <span class="panel-tag">{{ t("overviewTag") }}</span>
            <h2 class="section-title">{{ t("overviewTitle") }}</h2>
            <p class="section-copy">{{ t("overviewCopy") }}</p>
          </div>

          <div class="meaning-grid">
            <article class="meaning-card">
              <span>EN</span>
              <strong>{{ root.meaning_en || "-" }}</strong>
            </article>
            <article class="meaning-card">
              <span>JA</span>
              <strong>{{ root.meaning_ja || "-" }}</strong>
            </article>
            <article class="meaning-card">
              <span>ZH</span>
              <strong>{{ root.meaning_ch || "-" }}</strong>
            </article>
          </div>

          <div class="meta-card">
            <p><strong>{{ t("example") }}</strong> {{ root.example || "-" }}</p>
            <p><strong>{{ t("memo") }}</strong> {{ localizedMemo }}</p>
          </div>

          <button
            v-if="sameAsRoot"
            type="button"
            class="same-as-card"
            @click="openRoot(sameAsRoot.root_id)"
          >
            <span class="panel-tag">{{ t("sameAsTag") }}</span>
            <strong>{{ sameAsRoot.root_text }}</strong>
            <p>{{ getRootMeaning(sameAsRoot, lang) }}</p>
          </button>
        </article>

        <article class="panel-card">
          <div class="section-head compact-head">
            <span class="panel-tag">{{ t("videoTag") }}</span>
            <h2 class="section-title">{{ t("videoTitle") }}</h2>
            <p class="section-copy">{{ t("videoCopy") }}</p>
          </div>

          <div class="video-switches">
            <button
              type="button"
              :class="['switch-btn', { active: selectedPlatform === 'youtube' }]"
              @click="setPlatform('youtube')"
            >
              YouTube
            </button>
            <button
              type="button"
              :class="['switch-btn', { active: selectedPlatform === 'bilibili' }]"
              @click="setPlatform('bilibili')"
            >
              bilibili
            </button>
            <button
              type="button"
              :class="['switch-btn', { active: selectedMediaLang === 'ja' }]"
              @click="setMediaLang('ja')"
            >
              JA
            </button>
            <button
              type="button"
              :class="['switch-btn', { active: selectedMediaLang === 'zh' }]"
              @click="setMediaLang('zh')"
            >
              ZH
            </button>
          </div>

          <div v-if="currentVideo.embedUrl" class="video-frame-wrap">
            <iframe
              :src="currentVideo.embedUrl"
              class="video-frame"
              allowfullscreen
              referrerpolicy="strict-origin-when-cross-origin"
            />
          </div>
          <p v-else class="empty-state">{{ t("noVideo") }}</p>
          <a
            v-if="currentVideo.openUrl"
            :href="currentVideo.openUrl"
            target="_blank"
            rel="noopener"
            class="video-link"
          >
            {{ t("openVideo") }}
          </a>
        </article>
      </section>

      <section class="panel-card word-list-panel">
        <div class="section-head compact-head">
          <span class="panel-tag">{{ t("wordsTag") }}</span>
          <h2 class="section-title">{{ t("wordsTitle") }}</h2>
          <p class="section-copy">{{ t("wordsCopy") }}</p>
        </div>

        <div class="word-list">
          <button
            v-for="item in words"
            :key="item.word_id"
            type="button"
            class="word-item"
            @click="openWord(item.word_id)"
          >
            <div>
              <strong>{{ item.word_text }}</strong>
              <p>{{ getWordMeaning(item, lang) }}</p>
            </div>
            <span>{{ getPronunciation(item) }}</span>
          </button>
        </div>
      </section>
    </template>
  </section>
</template>

<script setup>
import axios from "axios";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useSiteLanguage } from "../../store/siteLanguage";
import {
  API_BASE,
  ensureApiObject,
  getPronunciation,
  getRootMeaning,
  getWordMeaning,
  recordRootVisit,
} from "./englishStudyShared";

const route = useRoute();
const router = useRouter();
const { lang, languages: langs, setLang } = useSiteLanguage();
const loading = ref(false);
const errorMessage = ref("");
const detail = ref({ root: null, same_as_root: null, words: [] });
const selectedPlatform = ref(localStorage.getItem("english_root_video_platform") || "youtube");
const selectedMediaLang = ref(localStorage.getItem("english_root_video_lang") || "ja");

const root = computed(() => detail.value.root || {});
const sameAsRoot = computed(() => detail.value.same_as_root || null);
const words = computed(() => (Array.isArray(detail.value.words) ? detail.value.words : []));

const currentVideo = computed(() => {
  const ids =
    selectedPlatform.value === "youtube"
      ? root.value.youtube_video_id || []
      : root.value.bilibili_video_id || [];
  const picked = selectedMediaLang.value === "zh" ? ids[1] || ids[0] : ids[0] || ids[1];
  if (!picked) {
    return { embedUrl: "", openUrl: "" };
  }

  if (selectedPlatform.value === "youtube") {
    return {
      embedUrl: `https://www.youtube.com/embed/${encodeURIComponent(picked)}`,
      openUrl: `https://www.youtube.com/watch?v=${encodeURIComponent(picked)}`,
    };
  }

  return {
    embedUrl: `https://player.bilibili.com/player.html?bvid=${encodeURIComponent(picked)}&page=1`,
    openUrl: `https://www.bilibili.com/video/${encodeURIComponent(picked)}`,
  };
});

const localizedMemo = computed(() => getLocalizedSlashText(root.value.memo, lang.value));

const i18n = {
  ja: {
    back: "Back",
    eyebrow: "ROOT DETAIL",
    loading: "Loading...",
    overviewTag: "OVERVIEW",
    overviewTitle: "語根の概要",
    overviewCopy: "意味、単語例、メモをまとめて確認できます。",
    example: "単語例",
    memo: "メモ",
    sameAsTag: "同じ語根",
    videoTag: "VIDEO",
    videoTitle: "関連動画",
    videoCopy: "プラットフォームを切り替えて、この語根に関連する動画を確認できます。",
    noVideo: "動画はまだありません。",
    openVideo: "新しいタブで開く",
    wordsTag: "WORD FAMILY",
    wordsTitle: "この語根の単語",
    wordsCopy: "この語根につながる単語を一覧で確認できます。",
  },
  zh: {
    back: "Back",
    eyebrow: "ROOT DETAIL",
    loading: "Loading...",
    overviewTag: "OVERVIEW",
    overviewTitle: "词根概览",
    overviewCopy: "可以一起查看含义、单词例和笔记。",
    example: "单词例",
    memo: "笔记",
    sameAsTag: "相同词根",
    videoTag: "VIDEO",
    videoTitle: "相关视频",
    videoCopy: "可以切换平台查看与这个词根相关的视频。",
    noVideo: "还没有视频。",
    openVideo: "在新标签页打开",
    wordsTag: "WORD FAMILY",
    wordsTitle: "该词根下的单词",
    wordsCopy: "这里可以查看所有与这个词根相关的单词。",
  },
  en: {
    back: "Back",
    eyebrow: "ROOT DETAIL",
    loading: "Loading...",
    overviewTag: "OVERVIEW",
    overviewTitle: "Root Overview",
    overviewCopy: "Review meanings, notes, and examples together.",
    example: "Example",
    memo: "Memo",
    sameAsTag: "sameAs",
    videoTag: "VIDEO",
    videoTitle: "Linked Video",
    videoCopy: "Switch between root-related videos by platform.",
    noVideo: "No video yet.",
    openVideo: "Open in new tab",
    wordsTag: "WORD FAMILY",
    wordsTitle: "Words Under This Root",
    wordsCopy: "A list of words linked to this root.",
  },
};

function t(key) {
  return i18n[lang.value]?.[key] || key;
}

function getLocalizedSlashText(value, currentLang) {
  const text = String(value || "").trim();
  if (!text) {
    return "-";
  }

  const parts = text.split("/").map((item) => item.trim());
  if (parts.length === 1) {
    return parts[0] || "-";
  }

  const [en = "", ja = "", zh = ""] = parts;
  if (currentLang === "en") {
    return en || ja || zh || "-";
  }

  if (currentLang === "zh") {
    return zh || ja || en || "-";
  }

  return ja || en || zh || "-";
}

function setPlatform(value) {
  selectedPlatform.value = value;
  localStorage.setItem("english_root_video_platform", value);
}

function setMediaLang(value) {
  selectedMediaLang.value = value;
  localStorage.setItem("english_root_video_lang", value);
}

async function loadRootDetail() {
  const key = decodeURIComponent(String(route.params.rootId || "")).trim();
  if (!key) {
    return;
  }

  loading.value = true;
  errorMessage.value = "";
  try {
    const response = await axios.get(`${API_BASE}/api/english/roots/${encodeURIComponent(key)}`, {
      timeout: 10000,
    });
    detail.value = ensureApiObject(response, "English root detail API");
    recordRootVisit(detail.value.root);
  } catch (error) {
    console.error(error);
    errorMessage.value = error?.message || "Failed to load root detail.";
  } finally {
    loading.value = false;
  }
}

function openRoot(rootId) {
  router.push(`/english/root/${encodeURIComponent(rootId)}`);
}

function openWord(wordId) {
  router.push(`/english/word/${encodeURIComponent(wordId)}`);
}

function goBack() {
  router.push("/english/englishdectionary");
}

onMounted(loadRootDetail);
watch(() => route.params.rootId, loadRootDetail);
</script>

<style scoped>
.root-detail-page {
  width: 100%;
  min-height: calc(100vh - 86px);
  padding: 18px;
  box-sizing: border-box;
  background:
    radial-gradient(circle at top left, rgba(14, 165, 233, 0.12), transparent 30%),
    radial-gradient(circle at bottom right, rgba(168, 85, 247, 0.12), transparent 25%),
    linear-gradient(180deg, #f7faff 0%, #eef4fb 100%);
  color: #163047;
}

.detail-hero,
.panel-card,
.state-card {
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 50px rgba(34, 73, 112, 0.08);
}

.detail-hero {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 18px;
  padding: 22px 24px;
  align-items: start;
}

.hero-eyebrow,
.panel-tag {
  margin: 0 0 8px;
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #35648e;
}

.hero-title,
.section-title {
  margin: 0;
  color: #10253a;
}

.hero-title {
  font-size: clamp(34px, 5vw, 60px);
  line-height: 0.95;
}

.hero-meta {
  margin-top: 12px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  color: #4f667a;
}

.back-btn,
.lang-chip,
.switch-btn,
.same-as-card,
.word-item {
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease;
}

.back-btn {
  border: 0;
  border-radius: 16px;
  padding: 12px 16px;
  background: #dceaf8;
  color: #133251;
  font-weight: 700;
}

.lang-pill {
  display: inline-flex;
  gap: 8px;
  padding: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(43, 90, 132, 0.14);
  justify-self: end;
}

.lang-chip {
  border: 0;
  min-width: 54px;
  padding: 10px 14px;
  border-radius: 999px;
  background: transparent;
  color: #47647d;
}

.lang-chip.active {
  background: linear-gradient(135deg, #0f4f8c, #1e7fc7);
  color: #fff;
}

.state-card {
  margin-top: 18px;
  padding: 20px;
}

.error-card {
  border-color: rgba(180, 35, 24, 0.18);
  background: rgba(255, 244, 242, 0.94);
  color: #7a271a;
}

.content-grid {
  margin-top: 18px;
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 18px;
}

.panel-card {
  padding: 22px;
}

.section-head {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}

.section-copy,
.meta-card p,
.same-as-card p,
.word-item p {
  margin: 0;
  color: #4c6377;
  line-height: 1.7;
}

.meaning-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.meaning-card,
.meta-card,
.same-as-card,
.word-item {
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 22px;
  background: #fbfdff;
  padding: 16px;
}

.meaning-card span {
  display: block;
  color: #4a6580;
  font-size: 13px;
}

.meaning-card strong {
  display: block;
  margin-top: 8px;
  color: #0f2840;
  font-size: 20px;
}

.meta-card,
.same-as-card {
  margin-top: 16px;
}

.same-as-card {
  width: 100%;
  text-align: left;
}

.video-switches {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.switch-btn {
  border: 0;
  border-radius: 12px;
  padding: 10px 12px;
  background: #dceaf8;
  color: #133251;
}

.switch-btn.active {
  background: #183b58;
  color: #fff;
}

.video-frame-wrap {
  margin-top: 16px;
  aspect-ratio: 16 / 9;
  width: 100%;
  background: #000;
  border-radius: 18px;
  overflow: hidden;
}

.video-frame {
  width: 100%;
  height: 100%;
  border: 0;
}

.video-link {
  display: inline-block;
  margin-top: 10px;
  color: #0f4f8c;
}

.word-list-panel {
  margin-top: 18px;
}

.word-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.word-item {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  text-align: left;
}

.empty-state {
  margin: 12px 0 0;
  color: #64748b;
}

.back-btn:hover,
.lang-chip:hover,
.switch-btn:hover,
.same-as-card:hover,
.word-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(25, 79, 121, 0.12);
}

@media (max-width: 1100px) {
  .detail-hero,
  .content-grid,
  .word-list {
    grid-template-columns: 1fr;
  }

  .lang-pill {
    justify-self: start;
  }
}

@media (max-width: 768px) {
  .root-detail-page {
    padding: 12px;
  }

  .detail-hero,
  .panel-card {
    padding: 18px;
    border-radius: 22px;
  }

  .meaning-grid {
    grid-template-columns: 1fr;
  }

  .word-item {
    flex-direction: column;
  }
}
</style>
